import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Star, Lock } from "lucide-react";

const CATEGORIES = [
  { key: "payment_on_time",             label: "Payment made on time" },
  { key: "professional_communication",  label: "Professional communication" },
  { key: "respect_to_staff",            label: "Respect shown to staff" },
  { key: "job_description_accuracy",    label: "Accuracy of job description" },
  { key: "sops_and_briefing",           label: "Quality of SOPs & briefing" },
  { key: "deployment_organisation",     label: "Organisation of deployment" },
  { key: "fair_treatment",              label: "Fair treatment" },
  { key: "site_safety",                 label: "Site safety" },
  { key: "overall_experience",          label: "Overall experience" },
];

function StarRow({ value, onChange, testIdBase }) {
  return (
    <div className="flex gap-1" data-testid={testIdBase}>
      {[1, 2, 3, 4, 5].map((s) => (
        <button key={s} type="button" onClick={() => onChange(s)} className="p-0.5" data-testid={`${testIdBase}-${s}`} aria-label={`${s} star`}>
          <Star className={`w-6 h-6 ${s <= value ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} strokeWidth={1.5} />
        </button>
      ))}
    </div>
  );
}

/**
 * Confidential company review form for staff.
 * Props: booking { id, company_id, company_name }, onDone()
 */
export default function CompanyReviewForm({ booking, onDone }) {
  const [ratings, setRatings] = useState(CATEGORIES.reduce((a, c) => ({ ...a, [c.key]: 5 }), {}));
  const [written, setWritten] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/company-reviews", {
        booking_id: booking.id,
        company_id: booking.company_id,
        written,
        ...ratings,
      });
      toast.success("Confidential review submitted. Thank you.");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const avg = (Object.values(ratings).reduce((a, b) => a + b, 0) / CATEGORIES.length).toFixed(1);

  return (
    <div className="rounded-xl border border-white/10 bg-[#0A0A0A] p-6" data-testid="company-review-form">
      <div className="flex flex-wrap justify-between items-start gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-outfit font-bold flex items-center gap-2">
            <Lock className="w-3 h-3" /> Confidential Company Review
          </div>
          <h3 className="text-xl font-outfit font-semibold text-white mt-1">Rate {booking.company_name || "the company"}</h3>
          <p className="text-sm text-white/60 font-manrope mt-1 max-w-xl">
            Your feedback is <strong>confidential</strong>. Companies see aggregated averages only — never your identity.
            Two or more serious concerns from independent professionals in 12 months auto-flag a company for admin review.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-outfit font-bold">Live avg</div>
          <div className="text-3xl font-outfit font-bold text-white">{avg}<span className="text-sm text-white/50">/5</span></div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {CATEGORIES.map((c) => (
          <div key={c.key} className="flex justify-between items-center border border-white/10 rounded-md px-3 py-2">
            <Label className="font-manrope text-sm">{c.label}</Label>
            <StarRow value={ratings[c.key]} onChange={(v) => setRatings({ ...ratings, [c.key]: v })} testIdBase={`co-review-${c.key}`} />
          </div>
        ))}
      </div>

      <div className="mt-4">
        <Label className="text-sm">Confidential written feedback (optional)</Label>
        <Textarea rows={3} value={written} onChange={(e) => setWritten(e.target.value)}
                  placeholder="Anything specific about how you were treated, paid, briefed or supported?" data-testid="co-review-written" />
      </div>

      <Button onClick={submit} disabled={busy} className="mt-4 bg-brand hover:bg-brand-hover text-white px-6 py-6 font-manrope font-semibold" data-testid="co-review-submit-btn">
        {busy ? "Submitting…" : "Submit Confidential Review"}
      </Button>
    </div>
  );
}
